/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/TasksProfessional.tsx
 * PURPOSE: Production task management — Manager Kanban + Employee task list
 *          2026-09-04 (employee information-architecture pass — the STRUCTURE
 *          the earlier same-day styling pass deliberately left alone):
 *          view NHÂN VIÊN (`!isManager`) bỏ hẳn cách nhóm theo PHÒNG BAN,
 *          chuyển sang 3 mục Quá hạn / Hôm nay / Đã xong đúng như màn mobile
 *          thật (mobile/src/screens/tasks/TaskListScreen.tsx:215-236 +
 *          :456-470). Vì sao đây là drift chứ không phải thiết kế đã duyệt:
 *          (a) SPEC-0418 (status: DRAFT — chưa ratify, và kể cả vậy vẫn đứng
 *          về phía mobile) §2.1 xếp Kanban vào **Manager**, §2.2 ghi Employee
 *          view = "Personal task list — sorted by priority"; (b) nhóm theo
 *          phòng ban ở view NV còn KHÔNG CHẠY — `department_name` chỉ có
 *          trong DashboardTaskItem (endpoint Manager), TaskResponse của
 *          GET /tasks/my không có trường đó (backend/app/api/v2/tasks.py
 *          _task_to_response), nên mọi nhiệm vụ của NV dồn vào một nhóm tên
 *          "Chưa phân phòng ban" — đã chụp màn hình xác nhận trước khi sửa.
 *          Kèm theo: (1) fetch của NV bỏ `shift_date=today` + `limit:50`
 *          (mobile getMyTasks({limit:50})) — bắt buộc, vì "Quá hạn" theo định
 *          nghĩa là việc NGÀY TRƯỚC; (2) quy tắc chia mục + sort tách thành
 *          hàm thuần export (`bucketEmployeeTasks`/`sortTasksByPriority`/
 *          `toLocalISODate`) có test thật:
 *          frontend/tests/pages/TasksProfessional.employeeSections.test.ts
 *          (12 test, đã mutation-verify đỏ được theo Debugging Pattern #15);
 *          (3) `toLocalISODate` vá lỗi lệch ngày UTC mà mobile đã sửa
 *          2026-07-27 (TaskListScreen.tsx:201-205) — tối giờ VN,
 *          `toISOString()` đã sang ngày mới nên việc "hôm nay" rơi nhầm
 *          "Quá hạn"; (4) markup từng dòng nhiệm vụ tách ra `renderTaskRow`
 *          dùng chung — KHÔNG đổi một ký tự nào bên trong, để Kanban Manager
 *          và 3 mục NV khỏi nhân đôi ~140 dòng JSX.
 *          KHÔNG đụng tới: Kanban của Manager (SPEC-0418 §2.1 xác nhận đó là
 *          thiết kế đúng cho Manager), chế độ "Lịch sử", và `embedded`.
 *          Chú ý về nút chuyển "Hôm nay"/"Lịch sử": nó VỐN ĐÃ `isManager &&`
 *          (chỉ Manager thấy, vì GET /tasks/history có _require_manager chặn
 *          non-manager) — NV chưa bao giờ nhìn thấy nút này, nên ở đây không
 *          có gì để gỡ; giữ nguyên cho Manager vì nó đọc dữ liệu THẬT KHÁC
 *          (query khoảng ngày tuỳ chọn tới 92 ngày qua /tasks/history), không
 *          phải bản lọc lại của cùng dữ liệu.
 *          2026-09-04 (mobile-layout-parity pass, employee route `/tasks`
 *          only — read-only audit against
 *          mobile/src/screens/tasks/TaskListScreen.tsx +
 *          mobile/src/screens/tasks/TaskDetailScreen.tsx; app-shell width +
 *          `.main` spacing wrapper structural changes are in
 *          TasksProfessional.module.css's own 2026-09-04 header entry):
 *          wrapped the "Hôm nay"/"Lịch sử" body (the two `viewMode === …`
 *          blocks) in a `<div className={styles.main}>` so it shares the
 *          `.main{display:flex;flex-direction:column;gap:var(--nqh-space-4)}`
 *          convention already applied to AttendanceModern.tsx/ScheduleModern.tsx
 *          this session — header/toast/modals/lightbox/footer stay outside
 *          it, same as those pages. No inline "Xem chi tiết"/task-detail
 *          route exists here (nor is one needed) — COMPLETED tasks already
 *          show their detail inline via `completionRow`
 *          (time + zoomable photo), mirroring what
 *          mobile/src/screens/tasks/TaskDetailScreen.tsx shows on its own
 *          separate screen; this page just doesn't need a second screen for
 *          it, same info, different (equally valid) presentation.
 *          Dozens of raw hex `style={{}}` colors (confirmed via grep before
 *          this edit — `#6b7280`, `#d97706`, `#dc2626`, `#f59e0b`, etc., used
 *          throughout instead of `var(--nqh-*)` tokens) replaced with their
 *          real design-token equivalents from
 *          frontend/src/styles/nqh-design-tokens.css. Exact hex matches
 *          (no approximation): `#d97706`→`--nqh-coffee-500` (PRIMARY, exact),
 *          `#92400e`→`--nqh-coffee-700`, `#451a03`→`--nqh-coffee-900`,
 *          `#fef7ee`→`--nqh-coffee-50`, `#fde8d3`→`--nqh-coffee-100`,
 *          `#fbcfa6`→`--nqh-coffee-200`, `#f9b079`→`--nqh-coffee-300`,
 *          `#dc2626`→`--nqh-chili-600` (ERROR, exact), `#b91c1c`→`--nqh-chili-700`,
 *          `#fef2f2`→`--nqh-chili-50`, `#fecaca`→`--nqh-chili-200`,
 *          `#f59e0b`→`--nqh-turmeric-500` (WARNING, exact), `#374151`→`--nqh-gray-700`
 *          (exact). Closest-family approximation where the raw hex was an
 *          improvised Tailwind gray not on this project's (different) gray
 *          scale: `#f9fafb`→`--nqh-gray-50`, `#f3f4f6`→`--nqh-gray-100`,
 *          `#d1d5db`→`--nqh-gray-300`, `#9ca3af`→`--nqh-gray-400`,
 *          `#6b7280`→`--nqh-gray-500`, `#1f2937`→`--nqh-gray-800`.
 *          REASSIGNED badge (`#a16207`/`#fefce8`) had no close match in the
 *          coffee/chili/turmeric families — approximated to
 *          `--nqh-turmeric-700`/`--nqh-turmeric-50` (same "warning, needs
 *          attention" semantic family). ESCALATED badge (`#7c3aed`/`#faf5ff`)
 *          is violet — the ONLY violet-family tokens in
 *          nqh-design-tokens.css are `--nqh-shift-night-fg`(#6d28d9)/
 *          `--nqh-shift-night-bg`(#ede9fe) (built for the night-shift pill,
 *          not escalation) — reused for hue-family consistency since no
 *          dedicated escalation/purple token exists; flagged inline. Plain
 *          `white`/black-overlay rgba() values (modal backdrops, photo-delete
 *          circle) are NOT brand-token drift (no NQH-specific hue) and were
 *          left as-is, consistent with the rest of this codebase's modal
 *          patterns.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-04
 * COMPLIANCE: SDLC 6.5.0 + SPEC-0713 + SPEC-0418 + Zero Mock Tolerance
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance | Rule 9: Documentation Standards
 *
 * @business-context
 * - Domain: Vietnamese F&B — Real-time task assignment during shifts
 * - Impact: Operational Critical — Manager assigns tasks, employees complete with evidence
 * - Stakeholders: Branch Managers, Shift Managers, Employees (NQH Holdings)
 *
 * @technical-architecture
 * - Layer: Presentation Layer (React 18 + CSS Modules)
 * - Pattern: Role-based rendering, apiV2 integration, Dual State Machine (AD_HOC + URGENT)
 * - Dependencies: /api/v2/tasks/*, /api/v1/team/members, useAuthStore
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/SPEC-0418-urgent-adhoc-task-ui-design.md
 * - Process: docs/02-design/06-process-workflows/SPEC-0713-URGENT-ADHOC-TASK-MANAGEMENT-DESIGN.md
 * - API: backend/app/api/v2/tasks.py
 * - BR: docs/01-planning/01-requirements/04-attendance/BR-ATTN-006-URGENT-ADHOC-TASK-MANAGEMENT.md
 * - Mobile parity source: mobile/src/screens/tasks/TaskListScreen.tsx +
 *   mobile/src/screens/tasks/TaskDetailScreen.tsx
 * - Related: frontend/src/pages/TasksProfessional.module.css
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { apiV2 } from '../services/api/config';
import { apiRequest } from '../services/api/config';
import { DateRangePicker } from '../components/ui/DateRangePicker';
import { toLocalISODate } from '../utils/date';
import styles from './TasksProfessional.module.css';

// =============================================================================
// Types — aligned with backend TaskResponse + DashboardResponse
// =============================================================================

interface TaskItem {
  id: string;
  task_type: 'AD_HOC' | 'URGENT';
  status: string;
  title: string;
  description: string | null;
  assigned_to: string;
  employee_name?: string;
  // 2026-08-17 (theo yêu cầu người dùng — nhóm Kanban theo Phòng ban): mới
  // nối vào backend (DashboardTaskItem.department_name — Organization qua
  // TenantUser.organization_id của người được giao, cùng nguồn vừa vá ở
  // Chấm công, KHÔNG phải field nào khác).
  department_name?: string | null;
  created_by: string;
  shift_date: string;
  deadline: string;
  acknowledged_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  completion_note: string | null;
  completion_photo_url: string | null;
  escalated_from: string | null;
  created_at: string;
}

interface DashboardColumn {
  count: number;
  tasks: TaskItem[];
}

interface DashboardResponse {
  shift_date: string;
  branch_id: string;
  columns: Record<string, DashboardColumn>;
  total_tasks: number;
}

interface TaskListResponse {
  items: TaskItem[];
  total: number;
  limit: number;
  offset: number;
}

interface StaffMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

// VĐ5 (2026-08-16): "Giao việc" trước đây chỉ xem được nhiệm vụ HÔM NAY
// (hardcode `shift_date=today`), không có cách nào xem lại nhiệm vụ ngày
// khác — không phải do người dùng nhầm, mà đúng là chưa từng nối vào FE dù
// backend đã có sẵn GET /tasks/history hỗ trợ date_from/date_to (S79-T1).
// Thêm chế độ "Lịch sử" dùng Date Range Picker, KHÔNG đụng vào chế độ
// "Hôm nay" (Kanban) hiện có — giữ nguyên toàn bộ logic/state cũ.
interface TaskHistoryItem {
  id: string;
  branch_id: string;
  task_type: 'AD_HOC' | 'URGENT';
  status: string;
  title: string;
  description: string | null;
  assigned_to: string;
  shift_date: string;
  deadline: string;
  completed_at: string | null;
  completion_note: string | null;
  // 2026-08-16: ảnh chứng minh hoàn thành — mới nối vào backend (xem
  // TaskHistoryItem trong tasks.py), dùng cho thiết kế lại "Quản lý Giao việc".
  completion_photo_url: string | null;
  created_at: string;
}

interface TaskHistoryResponse {
  items: TaskHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

// =============================================================================
// Constants — SPEC-0418 Section 8 Vietnamese labels
// =============================================================================

const STATUS_LABEL: Record<string, string> = {
  ASSIGNED: 'Đã giao',
  ACKNOWLEDGED: 'Đã nhận',
  IN_PROGRESS: 'Đang thực hiện',
  COMPLETED: 'Hoàn thành',
  OVERDUE: 'Quá hạn',
  ESCALATED: 'Đã nâng cấp',
  CLOSED: 'Đã đóng',
  REASSIGNED: 'Đã chuyển giao',
};

const MANAGER_ROLES = ['BRANCH_MANAGER', 'SHIFT_MANAGER', 'TENANT_ADMIN', 'TENANT_OWNER'];

// 2026-08-17 (theo yêu cầu người dùng — gõ tên lọc gợi ý khi chọn nhân viên
// giao việc): cùng cách bỏ dấu tiếng Việt đã dùng ở RangeAssignPopover.tsx/
// scheduleShared.ts (gõ không dấu vẫn khớp "Trần" khi gõ "tran").
const noDiacritics = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();

const DEADLINE_OPTIONS = [
  { label: '15 phút', minutes: 15 },
  { label: '30 phút', minutes: 30 },
  { label: '1 giờ', minutes: 60 },
  { label: '2 giờ', minutes: 120 },
  { label: '4 giờ', minutes: 240 },
  { label: 'Cuối ca (8 giờ)', minutes: 480 },
];

// =============================================================================
// EMPLOYEE SECTIONING (2026-09-04) — mobile parity
// -----------------------------------------------------------------------------
// Exported (not module-private) purely so the bucketing rule is unit-testable
// as a pure function — see TasksProfessional.employeeSections.test.ts. The
// rule below is NOT invented here: it is transcribed 1:1 from
// mobile/src/screens/tasks/TaskListScreen.tsx:205-236, the real screen
// employees use. Do not "improve" it in one place only — the two must stay
// identical or web and mobile disagree about what "quá hạn" means.
// =============================================================================

/** mobile TaskListScreen.tsx:209-213 `activeStatuses` — chưa hoàn thành,
 *  NV còn xử lý được. CHÚ Ý: OVERDUE cố tình KHÔNG nằm trong tập này (nó là
 *  điều kiện riêng của mục "Quá hạn" bên dưới); COMPLETED/ESCALATED/CLOSED/
 *  REASSIGNED cũng không — xem ghi chú "rơi ngoài 3 mục" ở bucketEmployeeTasks. */
export const EMPLOYEE_ACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'ASSIGNED',
  'ACKNOWLEDGED',
  'IN_PROGRESS',
]);

/**
 * SPEC-0418 §2.2 "Personal task list — sorted by priority" + SPEC-0713 AC-007:
 * KHẨN CẤP trước, rồi hạn tăng dần. Trùng đúng thứ tự backend đã sort sẵn
 * (GET /api/v2/tasks/my — `order_by(type_order, deadline.asc())`, tasks.py:1767-1775),
 * mà mobile dựa hẳn vào (nó không tự sort lại). Sort lại ở client là để thứ tự
 * KHÔNG phụ thuộc ngầm vào chi tiết cài đặt của endpoint.
 */
export function sortTasksByPriority<T extends { task_type: string; deadline: string }>(
  list: readonly T[],
): T[] {
  return [...list].sort((a, b) => {
    if (a.task_type === 'URGENT' && b.task_type !== 'URGENT') return -1;
    if (b.task_type === 'URGENT' && a.task_type !== 'URGENT') return 1;
    return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
  });
}

/**
 * Chia nhiệm vụ của NV vào 3 mục Quá hạn / Hôm nay / Đã xong —
 * **chép nguyên** mobile TaskListScreen.tsx:215-236:
 *
 *   overdue = status === OVERDUE  ||  (status ∈ active && shift_date < today)
 *   today   = status ∈ active     &&  shift_date === today
 *   done    = status === COMPLETED
 *
 * Rơi ngoài cả 3 mục (đúng như mobile, KHÔNG phải sót): nhiệm vụ active có
 * shift_date TƯƠNG LAI, và các trạng thái cuối ESCALATED/CLOSED/REASSIGNED —
 * những việc này không còn là việc NV phải làm hôm nay. Vì thế trang gọi
 * `/tasks/my` KHÔNG kèm `shift_date` (mobile: getMyTasks({limit:50})): mục
 * "Quá hạn" theo định nghĩa là việc của NGÀY TRƯỚC, lọc sẵn theo hôm nay ở
 * server thì mục đó vĩnh viễn rỗng.
 */
export function bucketEmployeeTasks<
  T extends { status: string; shift_date: string; task_type: string; deadline: string },
>(list: readonly T[], todayISO: string): { overdue: T[]; today: T[]; done: T[] } {
  const sorted = sortTasksByPriority(list);
  return {
    overdue: sorted.filter(
      (t) =>
        t.status === 'OVERDUE' ||
        (EMPLOYEE_ACTIVE_STATUSES.has(t.status) && t.shift_date < todayISO),
    ),
    today: sorted.filter(
      (t) => EMPLOYEE_ACTIVE_STATUSES.has(t.status) && t.shift_date === todayISO,
    ),
    done: sorted.filter((t) => t.status === 'COMPLETED'),
  };
}

// =============================================================================
// Component
// =============================================================================

interface TasksProfessionalProps {
  /** 2026-08-16: true khi render lồng trong ManagerLayout (route
   * /manager/tasks) — bỏ wrapper trang riêng (nền/padding/footer) vì
   * ManagerLayout.content đã lo phần đó (mirror ManagerAttendancePage.tsx),
   * để đồng bộ tông màu/kiểu chữ với các trang Manager khác + giữ sidebar.
   * false (mặc định) — route /tasks độc lập, EMPLOYEE dùng "Nhiệm vụ của
   * tôi" (KHÔNG có ManagerLayout) vẫn giữ nguyên giao diện trang đầy đủ cũ. */
  embedded?: boolean;
}

export const TasksProfessional: React.FC<TasksProfessionalProps> = ({ embedded = false }) => {
  const { isAuthenticated, userRole, accessToken } = useAuthStore();
  const navigate = useNavigate();

  // Role detection
  const isManager = MANAGER_ROLES.includes(userRole || '');

  // State
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTaskType, setCreateTaskType] = useState<'AD_HOC' | 'URGENT'>('AD_HOC');
  const [showCompleteModal, setShowCompleteModal] = useState<string | null>(null);
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // VĐ5: chế độ xem "Hôm nay" (Kanban, mặc định — không đổi logic cũ) vs
  // "Lịch sử" (Date Range Picker + GET /tasks/history, chỉ Manager).
  const [viewMode, setViewMode] = useState<'today' | 'history'>('today');
  const todayISO = toLocalISODate(new Date());
  const sevenDaysAgoISO = toLocalISODate(new Date(Date.now() - 6 * 86400000));
  const [historyFromISO, setHistoryFromISO] = useState(sevenDaysAgoISO);
  const [historyToISO, setHistoryToISO] = useState(todayISO);
  const [historyItems, setHistoryItems] = useState<TaskHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Create form state
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  // 2026-08-16 (theo yêu cầu người dùng): chọn NHIỀU nhân viên (trước đây
  // chỉ 1) — mảng id.
  const [formAssigneeIds, setFormAssigneeIds] = useState<string[]>([]);
  const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
  // 2026-08-17: gõ tên để lọc gợi ý trong panel chọn nhân viên
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [formDeadlineMinutes, setFormDeadlineMinutes] = useState(60);
  // Giao cả khoảng ngày (vd cả tuần) — mặc định hôm nay→hôm nay (giữ hành
  // vi cũ nếu không đổi). deadline_mode: MINUTES (như cũ, chỉ 1 ngày) hoặc
  // theo ca làm việc THẬT của từng NV/ngày (Đầu ca/Giữa ca/Kết ca).
  const [formDateFrom, setFormDateFrom] = useState(() => toLocalISODate(new Date()));
  const [formDateTo, setFormDateTo] = useState(() => toLocalISODate(new Date()));
  const [formDeadlineMode, setFormDeadlineMode] = useState<
    'MINUTES' | 'START_OF_SHIFT' | 'MID_SHIFT' | 'END_OF_SHIFT'
  >('MINUTES');

  // Complete form state
  const [completeNote, setCompleteNote] = useState('');
  const [completePhotoUrl, setCompletePhotoUrl] = useState('');

  // 2026-08-16 (thiết kế lại theo phản hồi người dùng): xem ảnh chứng minh
  // hoàn thành — bấm thumbnail mở lightbox, bấm lại để phóng to/thu nhỏ.
  const [zoomPhotoUrl, setZoomPhotoUrl] = useState<string | null>(null);
  const [zoomedIn, setZoomedIn] = useState(false);

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Toast auto-dismiss
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Show toast helper
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
  };

  // =========================================================================
  // Data fetching
  // =========================================================================

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const today = toLocalISODate(new Date());

      if (isManager) {
        // Manager: dashboard endpoint with Kanban columns
        const res = await apiV2.get<DashboardResponse>(`/tasks/dashboard?shift_date=${today}`);
        const allTasks: TaskItem[] = [];
        if (res.data.columns) {
          Object.values(res.data.columns).forEach((col) => {
            allTasks.push(...col.tasks);
          });
        }
        setTasks(allTasks);
      } else {
        // Employee: my tasks.
        // 2026-09-04 (mobile parity): bỏ `shift_date=${today}` — mobile gọi
        // getMyTasks({limit:50}) KHÔNG kèm shift_date (TaskListScreen.tsx:128-132).
        // Bắt buộc phải bỏ: mục "Quá hạn" định nghĩa là việc `shift_date < hôm
        // nay` (xem bucketEmployeeTasks), lọc sẵn đúng-hôm-nay ở server thì mục
        // đó không bao giờ có gì. `limit: 50` cũng lấy đúng con số của mobile
        // (mặc định endpoint chỉ 20 — tasks.py:1706).
        const res = await apiV2.get<TaskListResponse>('/tasks/my', {
          params: { limit: 50 },
        });
        setTasks(res.data.items || []);
      }
    } catch {
      showToast('Lỗi tải dữ liệu', 'error');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [isManager]);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await apiRequest<{ members?: StaffMember[]; items?: StaffMember[] }>(
        '/team/members?limit=100',
        { token: accessToken || undefined }
      );
      const list = res.members || res.items || (Array.isArray(res) ? res : []);
      setStaffList(list);
    } catch {
      // Staff list optional for employees
    }
  }, [accessToken]);

  useEffect(() => {
    fetchTasks();
    if (isManager) {
      fetchStaff();
    }
  }, [fetchTasks, fetchStaff, isManager]);

  // VĐ5: tải lịch sử nhiệm vụ theo khoảng ngày (Manager-only, backend
  // /tasks/history đã tự chặn non-manager bằng _require_manager).
  const fetchHistory = useCallback(async () => {
    if (!isManager) return;
    try {
      setHistoryLoading(true);
      const res = await apiV2.get<TaskHistoryResponse>('/tasks/history', {
        params: { date_from: historyFromISO, date_to: historyToISO, limit: 100 },
      });
      setHistoryItems(res.data.items || []);
      setHistoryTotal(res.data.total || 0);
    } catch {
      showToast('Lỗi tải lịch sử nhiệm vụ', 'error');
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [isManager, historyFromISO, historyToISO]);

  useEffect(() => {
    if (viewMode === 'history') {
      fetchHistory();
    }
  }, [viewMode, fetchHistory]);

  const employeeNameById = (id: string): string => {
    const found = staffList.find((s) => s.id === id);
    return found ? found.full_name : `NV #${id.slice(0, 8)}`;
  };

  // =========================================================================
  // Actions — SPEC-0713 state transitions
  // =========================================================================

  const handleAcknowledge = async (taskId: string) => {
    setActionLoading(taskId);
    try {
      await apiV2.put(`/tasks/${taskId}/acknowledge`);
      showToast('Đã xác nhận nhiệm vụ', 'success');
      fetchTasks();
    } catch {
      showToast('Lỗi xác nhận', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStart = async (taskId: string) => {
    setActionLoading(taskId);
    try {
      await apiV2.put(`/tasks/${taskId}/start`);
      showToast('Đã bắt đầu nhiệm vụ', 'success');
      fetchTasks();
    } catch {
      showToast('Lỗi bắt đầu', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    setActionLoading(taskId);
    try {
      await apiV2.put(`/tasks/${taskId}/complete`, {
        completion_note: completeNote || null,
        completion_photo_url: completePhotoUrl || null,
      });
      showToast('Đã hoàn thành nhiệm vụ!', 'success');
      setShowCompleteModal(null);
      setCompleteNote('');
      setCompletePhotoUrl('');
      fetchTasks();
    } catch {
      showToast('Lỗi hoàn thành', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEscalate = async (taskId: string) => {
    if (!window.confirm('Nâng cấp nhiệm vụ này lên KHẨN CẤP?')) return;
    setActionLoading(taskId);
    try {
      await apiV2.put(`/tasks/${taskId}/escalate`);
      showToast('Đã nâng cấp lên Khẩn cấp!', 'success');
      fetchTasks();
    } catch {
      showToast('Lỗi escalate', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClose = async (taskId: string) => {
    const reason = window.prompt('Lý do đóng nhiệm vụ:');
    if (!reason) return;
    setActionLoading(taskId);
    try {
      await apiV2.patch(`/tasks/${taskId}/close`, { reason });
      showToast('Đã đóng nhiệm vụ', 'success');
      fetchTasks();
    } catch {
      showToast('Lỗi đóng task', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReassign = async (taskId: string) => {
    const newAssignee = window.prompt('ID nhân viên mới:');
    if (!newAssignee) return;
    const reason = window.prompt('Lý do chuyển giao:');
    if (!reason) return;
    setActionLoading(taskId);
    try {
      await apiV2.patch(`/tasks/${taskId}/reassign`, {
        new_assigned_to: newAssignee,
        reason,
      });
      showToast('Đã chuyển giao nhiệm vụ', 'success');
      fetchTasks();
    } catch {
      showToast('Lỗi chuyển giao', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // =========================================================================
  // Create Task — SPEC-0418 Section 4.2
  // =========================================================================

  const handleCreateTask = async () => {
    if (!formTitle.trim() || formAssigneeIds.length === 0) {
      showToast('Vui lòng nhập tiêu đề và chọn ít nhất 1 nhân viên', 'error');
      return;
    }
    if (formDateTo < formDateFrom) {
      showToast('Ngày kết thúc phải sau hoặc bằng ngày bắt đầu', 'error');
      return;
    }
    if (formDeadlineMode === 'MINUTES' && formDateFrom !== formDateTo) {
      showToast('Đặt hạn theo phút chỉ áp dụng cho 1 ngày — chọn Đầu ca/Giữa ca/Kết ca để giao nhiều ngày', 'error');
      return;
    }

    setActionLoading('create');
    try {
      // 2026-08-16: đổi sang /tasks/bulk-create (N nhân viên × khoảng ngày) —
      // thay hẳn POST /tasks đơn cũ, xử lý đúng cả trường hợp 1 NV/1 ngày cũ
      // (employee_ids=[1 phần tử], date_start=date_end=hôm nay).
      const res = await apiV2.post<{ created_count: number; skipped: { employee_id: string; employee_name: string; date: string; reason: string }[] }>(
        '/tasks/bulk-create',
        {
          task_type: createTaskType,
          title: formTitle.trim(),
          description: formDescription.trim() || null,
          assigned_to_ids: formAssigneeIds,
          date_start: formDateFrom,
          date_end: formDateTo,
          deadline_mode: formDeadlineMode,
          deadline_minutes: formDeadlineMode === 'MINUTES' ? formDeadlineMinutes : null,
        },
      );

      const { created_count, skipped } = res.data;
      if (created_count === 0) {
        showToast(
          skipped.length > 0
            ? `Không giao được nhiệm vụ nào — ${skipped[0].reason}`
            : 'Không giao được nhiệm vụ nào',
          'error',
        );
      } else {
        const typeLabel = createTaskType === 'URGENT' ? 'Khẩn cấp' : 'Phát sinh';
        showToast(
          skipped.length > 0
            ? `Đã giao ${created_count} nhiệm vụ ${typeLabel} — bỏ qua ${skipped.length} (NV không có ca/chưa có chi nhánh)`
            : `Đã giao ${created_count} nhiệm vụ ${typeLabel}!`,
          'success',
        );
      }

      // Reset form
      setFormTitle('');
      setFormDescription('');
      setFormAssigneeIds([]);
      setAssigneeSearch('');
      setFormDeadlineMinutes(60);
      setFormDateFrom(toLocalISODate(new Date()));
      setFormDateTo(toLocalISODate(new Date()));
      setFormDeadlineMode('MINUTES');
      setShowCreateModal(false);
      fetchTasks();
    } catch {
      showToast('Lỗi tạo nhiệm vụ', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  // =========================================================================
  // Helpers — SPEC-0418 Section 3 design tokens
  // =========================================================================

  // 2026-08-16 (theo phản hồi người dùng): bỏ xanh dương (ACKNOWLEDGED,
  // badge PHÁT SINH, viền ô mặc định) + xanh lá (IN_PROGRESS, COMPLETED,
  // toast/nút thành công) — chỉ dùng thang màu thương hiệu cam-coffee
  // (--nqh-coffee-*). Đỏ (KHẨN CẤP/OVERDUE) giữ nguyên — không nằm trong
  // 2 màu người dùng nêu, và cần nổi bật cho trạng thái khẩn/quá hạn.
  const getStatusBadgeStyle = (status: string): React.CSSProperties => {
    const map: Record<string, { color: string; bg: string }> = {
      ASSIGNED: { color: 'var(--nqh-coffee-700)', bg: 'var(--nqh-coffee-50)' },
      ACKNOWLEDGED: { color: 'var(--nqh-coffee-500)', bg: 'var(--nqh-coffee-100)' },
      IN_PROGRESS: { color: 'var(--nqh-coffee-700)', bg: 'var(--nqh-coffee-200)' },
      COMPLETED: { color: 'var(--nqh-coffee-900)', bg: 'var(--nqh-coffee-50)' },
      OVERDUE: { color: 'var(--nqh-chili-700)', bg: 'var(--nqh-chili-50)' },
      // ESCALATED — violet: no dedicated escalation token exists in
      // nqh-design-tokens.css; reused the only violet-family pair
      // (--nqh-shift-night-fg/bg, built for the night-shift pill) for hue
      // consistency rather than leaving raw hex. See file header note.
      ESCALATED: { color: 'var(--nqh-shift-night-fg)', bg: 'var(--nqh-shift-night-bg)' },
      CLOSED: { color: 'var(--nqh-gray-500)', bg: 'var(--nqh-gray-100)' },
      // REASSIGNED — approximated to the turmeric (warning) family, closest
      // semantic match; original #a16207/#fefce8 had no exact token. See
      // file header note.
      REASSIGNED: { color: 'var(--nqh-turmeric-700)', bg: 'var(--nqh-turmeric-50)' },
    };
    const s = map[status] || { color: 'var(--nqh-gray-500)', bg: 'var(--nqh-gray-100)' };
    return { color: s.color, background: s.bg, borderColor: s.color };
  };

  const getTaskBorderColor = (task: TaskItem): string => {
    if (task.task_type === 'URGENT') return 'var(--nqh-chili-600)';
    if (task.status === 'COMPLETED') return 'var(--nqh-coffee-500)';
    if (task.status === 'OVERDUE') return 'var(--nqh-chili-600)';
    return 'var(--nqh-coffee-300)';
  };

  const formatDeadline = (deadline: string): string => {
    try {
      return new Date(deadline).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return deadline;
    }
  };

  // BUG THẬT phát hiện khi test (2026-08-16): một số completion_photo_url
  // trong DB bị lưu dạng CHUỖI JSON BỌC MẢNG (vd `["data:image/...=="]`)
  // thay vì chuỗi data-URL thường — gán thẳng vào <img src> sẽ ra ảnh vỡ.
  // Không rõ nguồn gốc chính xác (dữ liệu demo/test cũ), vá phòng thủ ở đây
  // để hiện đúng bất kể định dạng lưu trữ nào đã có trong DB.
  const normalizePhotoUrl = (raw: string): string => {
    const trimmed = raw.trim();
    if (trimmed.startsWith('["') && trimmed.endsWith('"]')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && typeof parsed[0] === 'string') return parsed[0];
      } catch {
        // giữ nguyên chuỗi gốc nếu parse thất bại
      }
    }
    return raw;
  };

  const formatCompletedAt = (iso: string): string => {
    try {
      return new Date(iso).toLocaleString('vi-VN', {
        hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  // =========================================================================
  // Render
  // =========================================================================

  /**
   * Một dòng nhiệm vụ. 2026-09-04: tách ra từ thân `.map()` cũ — markup bên
   * trong KHÔNG đổi một ký tự nào (kể cả các nhánh nút hành động `isManager`/
   * `!isManager`), chỉ để Kanban-theo-phòng-ban (Manager) và 3 mục
   * Quá hạn/Hôm nay/Đã xong (NV) dùng CHUNG một dòng, khỏi nhân đôi ~140 dòng
   * JSX. Mobile cũng có đúng hàm này với đúng vai trò:
   * TaskListScreen.tsx:298 `renderTaskRow`.
   */
  const renderTaskRow = (task: TaskItem) => (
    <div
      key={task.id}
      className={styles.taskItem}
      style={{ borderLeftColor: getTaskBorderColor(task) }}
    >
      <div className={styles.taskContent}>
        <div className={styles.taskMain}>
          {/* Task Header — title + type badge + status badge */}
          <div className={styles.taskHeader}>
            <h4 className={styles.taskTitle}>{task.title}</h4>

            {/* Task Type Badge — SPEC-0418 Section 3.1 */}
            <span className={styles.badge} style={{
              background: task.task_type === 'URGENT' ? 'var(--nqh-chili-600)' : 'var(--nqh-coffee-500)',
              color: 'var(--nqh-white)',
              borderColor: task.task_type === 'URGENT' ? 'var(--nqh-chili-600)' : 'var(--nqh-coffee-500)',
            }}>
              {task.task_type === 'URGENT' ? 'KHẨN CẤP' : 'PHÁT SINH'}
            </span>

            {/* Status Badge — SPEC-0418 Section 3.2 */}
            <span className={styles.badge} style={getStatusBadgeStyle(task.status)}>
              {STATUS_LABEL[task.status] || task.status}
            </span>
          </div>

          {/* Description */}
          {task.description && (
            <p className={styles.taskDescription}>{task.description}</p>
          )}

          {/* Meta — SPEC-0418 Section 4.1 */}
          <div className={styles.taskMeta}>
            <span>⏰ Hạn: {formatDeadline(task.deadline)}</span>
            {task.employee_name && <span>👤 {task.employee_name}</span>}
          </div>

          {/* 2026-08-16: thông tin hoàn thành thật (thay 4 thẻ
              thống kê cũ) — khi nào xong + ảnh chứng minh NV chụp
              lúc hoàn thành, bấm thumbnail để zoom xem. */}
          {task.status === 'COMPLETED' && (
            <div className={styles.completionRow}>
              {task.completed_at && (
                <span className={styles.completionTime}>
                  ✅ Hoàn thành lúc {formatCompletedAt(task.completed_at)}
                </span>
              )}
              {task.completion_photo_url ? (
                <button
                  type="button"
                  className={styles.completionThumbBtn}
                  onClick={() => { setZoomPhotoUrl(normalizePhotoUrl(task.completion_photo_url as string)); setZoomedIn(false); }}
                >
                  <img
                    src={normalizePhotoUrl(task.completion_photo_url)}
                    alt="Ảnh chứng minh hoàn thành"
                    className={styles.completionThumb}
                  />
                  <span className={styles.completionThumbLabel}>🔍 Xem ảnh</span>
                </button>
              ) : (
                <span className={styles.completionNoPhoto}>Không có ảnh</span>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons — SPEC-0418 Section 4.3 table */}
        <div className={styles.taskActions}>
          {/* Employee actions */}
          {!isManager && task.status === 'ASSIGNED' && (
            <button
              className={styles.actionButton}
              disabled={actionLoading === task.id}
              onClick={() => handleAcknowledge(task.id)}
            >
              {actionLoading === task.id ? 'Đang xử lý...' : 'Đã nhận'}
            </button>
          )}
          {!isManager && task.status === 'ACKNOWLEDGED' && (
            <button
              className={styles.actionButton}
              disabled={actionLoading === task.id}
              onClick={() => handleStart(task.id)}
            >
              {actionLoading === task.id ? 'Đang xử lý...' : 'Bắt đầu'}
            </button>
          )}
          {!isManager && task.status === 'IN_PROGRESS' && (
            <button
              className={styles.actionButton}
              style={{ background: 'var(--nqh-coffee-500)' }}
              disabled={actionLoading === task.id}
              onClick={() => {
                setCompleteNote('');
                setCompletePhotoUrl('');
                setShowCompleteModal(task.id);
              }}
            >
              {actionLoading === task.id ? 'Đang xử lý...' : 'Hoàn thành'}
            </button>
          )}

          {/* Manager actions — SPEC-0418 Section 4.5 / 4.6 */}
          {isManager && task.task_type === 'AD_HOC' &&
            (task.status === 'ACKNOWLEDGED' || task.status === 'IN_PROGRESS') && (
            <button
              className={styles.actionButton}
              style={{ background: 'var(--nqh-chili-600)' }}
              disabled={actionLoading === task.id}
              onClick={() => handleEscalate(task.id)}
            >
              {actionLoading === task.id ? 'Đang xử lý...' : 'Nâng cấp'}
            </button>
          )}
          {isManager && task.status === 'OVERDUE' && (
            <>
              <button
                className={styles.actionButton}
                style={{ background: 'var(--nqh-gray-500)' }}
                disabled={actionLoading === task.id}
                onClick={() => handleClose(task.id)}
              >
                Đóng
              </button>
              <button
                className={styles.actionButton}
                style={{ background: 'var(--nqh-turmeric-500)' }}
                disabled={actionLoading === task.id}
                onClick={() => handleReassign(task.id)}
              >
                Giao lại
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  /**
   * 2026-09-04 — 3 mục của NV. Mỗi mục = 1 thẻ + tiêu đề "<Tên> (N)", khớp
   * mobile `<Card><CardHeader title={`Quá hạn (${overdue.length})`} /></Card>`
   * (TaskListScreen.tsx:456-470). Khoảng cách giữa các thẻ do
   * `.main{gap:--nqh-space-4}` lo, tương đương `marginTop: spacing.md` của mobile.
   */
  const renderEmployeeSection = (title: string, list: TaskItem[]) =>
    list.length === 0 ? null : (
      <div className={styles.tasksCard} key={title}>
        <h3 className={styles.tasksTitle}>{`${title} (${list.length})`}</h3>
        <div className={styles.tasksList}>{list.map(renderTaskRow)}</div>
      </div>
    );

  // Chỉ tính cho NV — Manager giữ nguyên Kanban nhóm theo phòng ban.
  const employeeBuckets = isManager
    ? null
    : bucketEmployeeTasks(tasks, toLocalISODate(new Date()));

  return (
    <div className={embedded ? undefined : styles.page}>
      {/* Toast — SPEC-0418 Section 3.6 */}
      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 10001,
          padding: '12px 20px', borderRadius: '8px', color: 'white',
          background: toast.type === 'success' ? 'var(--nqh-coffee-500)' : 'var(--nqh-chili-600)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', fontSize: '14px', fontWeight: 500,
        }}>
          {toast.message}
        </div>
      )}

      {/* 2026-09-04 (nav-affordance fix): this route had NO way in/out —
          confirmed no <EmployeeBottomNav> and no header back button, a dead
          end once landed on `/tasks`. Per explicit user instruction, do NOT
          bolt on <EmployeeBottomNav> — the bottom nav is ratified/locked at
          exactly 5 items (Trang chủ/Lịch/Chấm công/Tiện ích/Tôi) and `/tasks`
          is not one of them; none of the 5 tabs could correctly show
          "active" for this route, and it would misrepresent this page as a
          6th tab-root when it isn't one. Mirrors mobile exactly:
          mobile/src/screens/tasks/TaskListScreen.tsx:424-427 uses
          `<AppHeader title="Nhiệm vụ của tôi" onBackPress={...} />` — a
          single header back button, NO bottom tab bar, because on mobile
          this is a stack-pushed screen (reached by navigating INTO it from a
          tab-root screen), not a tab-root itself. Same pattern already
          applied this session to AttendanceHistory.tsx/LeaveRequestModern.tsx
          (also secondary/non-tab employee pages).
          Fixed destination, NOT navigate(-1)/history-based back — same
          discipline as ProfilePage.tsx's ISS-EMP-11 fix (history-back is
          fragile: breaks on direct URL / new tab arrival). Back-target
          chosen: `/utilities` (Tiện ích) — Utilities.tsx:170 has the
          "Nhiệm vụ của tôi" tile that navigates INTO `/tasks`, so Tiện ích
          is this screen's semantic home (mirrors mobile's own bottom-tab
          equivalent of this feature living under "Tiện ích" per this
          session's own earlier UI audit). Route confirmed live at
          App.tsx:661-665 (RequireRole ALL_TENANT_ROLES — same guard tier as
          this page's other employee-facing siblings).
          Manager-facing markup (toolbar/mode-toggle/"+ Giao việc") below is
          completely untouched — Manager reaches this page with its own nav
          chrome (ManagerLayout sidebar) and this row is employee-only
          (`!isManager && !embedded`). */}
      {!isManager && !embedded && (
        <div className={styles.employeeHeaderRow}>
          <button
            type="button"
            onClick={() => navigate('/utilities')}
            className={styles.employeeHeaderBackButton}
            aria-label="Quay lại"
            data-testid="tasks-header-back"
          >
            <ChevronLeft size={24} aria-hidden="true" />
          </button>
          <div className={styles.employeeHeaderTitleWrap}>
            <h1 className={styles.title} data-testid="tasks-header-title">Nhiệm vụ của tôi</h1>
          </div>
        </div>
      )}

      {/* Header — SPEC-0418 Section 4.1 / 4.3 (VĐ5: gọn lại thành 1 toolbar,
          không đổi hành vi nút "+ Giao việc" cũ) */}
      <div className={styles.headerSection}>
        <div className={styles.toolbar}>
          <div>
            {/* 2026-09-04: employee's h1 moved into the new employeeHeaderRow
                above (single-row back+title, mirrors AttendanceHistory.tsx) —
                rendering it again here would duplicate "Nhiệm vụ của tôi" on
                the page. Manager's h1 is untouched (still renders here,
                unchanged — Manager doesn't get the new header row). */}
            {isManager && <h1 className={styles.title}>📋 Quản lý Giao việc</h1>}
            {/* 2026-09-04: phụ đề của NV cũ ghi "…trong ca — <ngày hôm nay>",
                nhưng từ lần sửa này danh sách KHÔNG còn bó trong hôm nay
                (mục "Quá hạn" kéo cả việc ngày trước, "Đã xong" cả việc đã
                hoàn thành) — để nguyên là mô tả sai. Manager giữ nguyên câu cũ. */}
            <p className={styles.subtitle}>
              {isManager
                ? 'Giao và theo dõi nhiệm vụ cho nhân viên'
                : 'Việc quá hạn, việc hôm nay và việc đã xong của bạn'
              }
            </p>
          </div>

          <div className={styles.toolbarActions}>
            {/* VĐ5: chuyển "Hôm nay" (Kanban, mặc định) / "Lịch sử" (Date
                Range Picker) — chỉ Manager, vì /tasks/history yêu cầu Manager */}
            {isManager && (
              <div className={styles.modeToggle} role="tablist" aria-label="Chế độ xem nhiệm vụ">
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'today'}
                  className={`${styles.modeButton} ${viewMode === 'today' ? styles.modeButtonActive : ''}`}
                  onClick={() => setViewMode('today')}
                >
                  Hôm nay
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={viewMode === 'history'}
                  className={`${styles.modeButton} ${viewMode === 'history' ? styles.modeButtonActive : ''}`}
                  onClick={() => setViewMode('history')}
                >
                  Lịch sử
                </button>
              </div>
            )}

            {/* Manager: Single entry point — SPEC-0418 v1.1.0 §4.1 (RPT-GOV-041 §8) */}
            {isManager && (
              <button
                className={styles.createButton}
                onClick={() => { setCreateTaskType('AD_HOC'); setShowCreateModal(true); }}
              >
                + Giao việc
              </button>
            )}
          </div>
        </div>

        {/* VĐ5: Date Range Picker — chỉ hiện ở chế độ Lịch sử */}
        {isManager && viewMode === 'history' && (
          <div className={styles.historyDateBar}>
            <DateRangePicker
              value={{ start: historyFromISO, end: historyToISO }}
              onChange={(range) => {
                setHistoryFromISO(range.start ?? sevenDaysAgoISO);
                setHistoryToISO(range.end ?? range.start ?? todayISO);
              }}
              maxSpanDays={92}
            />
          </div>
        )}
      </div>

      {/* 2026-09-04 mobile-layout-parity: shared-spacing wrapper — same
          `.main{display:flex;flex-direction:column;gap:var(--nqh-space-4)}`
          convention as AttendanceModern.tsx/ScheduleModern.tsx this session.
          Wraps only the "Hôm nay"/"Lịch sử" body — header/toast/modals/
          lightbox/footer stay outside it, same as those pages. */}
      <div className={styles.main}>
      {/* VĐ5: Kanban chỉ áp dụng chế độ "Hôm nay" — dữ liệu `tasks` luôn là
          của hôm nay, không đổi logic tính toán cũ. */}
      {viewMode === 'today' && (
      <>
      {/* 2026-08-16 (theo phản hồi người dùng): 4 thẻ thống kê cũ (Tổng/Chờ
          xử lý/Đang thực hiện/Hoàn thành) đã BỎ — chỉ là con số trần, không
          giúp quản lý làm gì cả. Thay bằng thông tin thật hữu ích ngay trên
          từng dòng nhiệm vụ: khi nào hoàn thành + ảnh chứng minh NV chụp lúc
          xong (xem completedInfo/renderCompletionPhoto bên dưới) — quản lý
          xem/zoom ảnh trực tiếp, không cần đoán qua con số tổng hợp. */}

      {/* =====================================================================
          NHÂN VIÊN (2026-09-04) — 3 mục Quá hạn / Hôm nay / Đã xong.
          -----------------------------------------------------------------
          Trước đây NV dùng CHUNG khối Kanban-nhóm-theo-phòng-ban của Manager
          bên dưới. Đó là drift thật, không phải thiết kế đã duyệt:
          • mobile — màn NV thật (TaskListScreen.tsx:456-470) — là danh sách 3
            mục theo thời gian/trạng thái, KHÔNG có phòng ban, KHÔNG có Kanban;
          • SPEC-0418 (status: DRAFT) §2.1 xếp Kanban vào **Manager**, còn §2.2
            ghi rõ Employee view = "Personal task list — sorted by priority";
          • và nhóm theo phòng ban ở view NV còn KHÔNG CHẠY: `department_name`
            chỉ có trong DashboardTaskItem (endpoint Manager). TaskResponse của
            GET /tasks/my không hề có trường đó (backend/app/api/v2/tasks.py
            _task_to_response), nên 100% nhiệm vụ của NV rơi vào một nhóm duy
            nhất tên "Chưa phân phòng ban" — một tiêu đề vô nghĩa (đã chụp màn
            hình xác nhận trước khi sửa), không phải cách nhóm.
          Kanban của Manager bên dưới KHÔNG bị đụng tới (SPEC-0418 §2.1 xác
          nhận đó mới là thiết kế đúng cho Manager).
          ===================================================================== */}
      {!isManager && employeeBuckets && (
        loading ? (
          <div className={styles.tasksCard}>
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--nqh-gray-500)' }}>
              Đang tải...
            </div>
          </div>
        ) : employeeBuckets.overdue.length === 0 &&
          employeeBuckets.today.length === 0 &&
          employeeBuckets.done.length === 0 ? (
          /* Empty State — mobile TaskListScreen.tsx:452-453 dùng
             `items.length === 0`; ở đây xét "cả 3 mục đều rỗng" vì đó là tập
             cha (bao gồm luôn trường hợp chỉ còn nhiệm vụ ngày tương lai /
             ESCALATED / CLOSED / REASSIGNED — mobile sẽ hiện một trang trắng
             không giải thích gì, ở đây vẫn hiện được lời nhắn). */
          <div className={styles.tasksCard}>
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--nqh-gray-500)' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
              <p>Không có nhiệm vụ. Bạn đã hoàn thành tất cả!</p>
            </div>
          </div>
        ) : (
          <>
            {renderEmployeeSection('Quá hạn', employeeBuckets.overdue)}
            {renderEmployeeSection('Hôm nay', employeeBuckets.today)}
            {renderEmployeeSection('Đã xong', employeeBuckets.done)}
          </>
        )
      )}

      {/* Task List (Manager Kanban) — SPEC-0418 Section 4.1 / 4.3 §2.1 */}
      {isManager && (
      <div className={styles.tasksCard}>
        <h3 className={styles.tasksTitle}>
          {`Danh sách nhiệm vụ (${new Date().toLocaleDateString('vi-VN')})`}
        </h3>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--nqh-gray-500)' }}>
            Đang tải...
          </div>
        ) : tasks.length === 0 ? (
          /* Empty State — SPEC-0418 Section 4.1 / 4.3 */
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--nqh-gray-500)' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
            {/* 2026-09-04: khối này giờ chỉ Manager render (`isManager &&`
                bên trên) — nhánh NV của câu ternary cũ đã chuyển sang khối
                3-mục phía trên, giữ nguyên nguyên văn câu chữ. */}
            <p>Chưa có nhiệm vụ nào hôm nay. Nhấn 'Tạo việc' để giao việc cho nhân viên.</p>
          </div>
        ) : (
          <div className={styles.tasksList}>
            {/* Sort: URGENT first, then by deadline — SPEC-0713 AC-007.
                2026-08-17 (theo yêu cầu người dùng): nhóm theo Phòng ban
                (department_name — Organization qua TenantUser.organization_id
                của người được giao, cùng nguồn vừa vá ở Chấm công) — bọc
                ngoài .map cũ, KHÔNG đổi JSX từng dòng nhiệm vụ bên trong. */}
            {(() => {
              const sorted = [...tasks].sort((a, b) => {
                if (a.task_type === 'URGENT' && b.task_type !== 'URGENT') return -1;
                if (b.task_type === 'URGENT' && a.task_type !== 'URGENT') return 1;
                return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
              });
              const deptGroups = new Map<string, TaskItem[]>();
              sorted.forEach((t) => {
                const dept = t.department_name || 'Chưa phân phòng ban';
                if (!deptGroups.has(dept)) deptGroups.set(dept, []);
                deptGroups.get(dept)!.push(t);
              });
              return Array.from(deptGroups.entries()).map(([deptName, deptTasks]) => (
                <div key={deptName} className={styles.deptGroup}>
                  <div className={styles.deptGroupHead}>
                    {deptName} <span className={styles.deptGroupCount}>({deptTasks.length})</span>
                  </div>
                  {deptTasks.map(renderTaskRow)}
                </div>
              ));
            })()}
          </div>
        )}
      </div>
      )}
      </>
      )}

      {/* VĐ5: Lịch sử nhiệm vụ — chỉ Manager, đọc GET /tasks/history theo
          khoảng ngày đã chọn ở Date Range Picker phía trên. Danh sách chỉ
          xem (không có nút hành động — lịch sử không đổi trạng thái). */}
      {isManager && viewMode === 'history' && (
        <div className={styles.tasksCard}>
          <h3 className={styles.tasksTitle}>
            Lịch sử nhiệm vụ ({historyTotal} kết quả)
          </h3>

          {historyLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--nqh-gray-500)' }}>
              Đang tải...
            </div>
          ) : historyItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--nqh-gray-500)' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
              <p>Không có nhiệm vụ nào trong khoảng ngày đã chọn.</p>
            </div>
          ) : (
            <div className={styles.tasksList}>
              {historyItems.map((task) => (
                <div
                  key={task.id}
                  className={styles.taskItem}
                  style={{
                    borderLeftColor:
                      task.task_type === 'URGENT' ? 'var(--nqh-chili-600)'
                      : task.status === 'COMPLETED' ? 'var(--nqh-coffee-500)'
                      : task.status === 'OVERDUE' ? 'var(--nqh-chili-600)'
                      : 'var(--nqh-coffee-300)',
                  }}
                >
                  <div className={styles.taskContent}>
                    <div className={styles.taskMain}>
                      <div className={styles.taskHeader}>
                        <h4 className={styles.taskTitle}>{task.title}</h4>
                        <span className={styles.badge} style={{
                          background: task.task_type === 'URGENT' ? 'var(--nqh-chili-600)' : 'var(--nqh-coffee-500)',
                          color: 'var(--nqh-white)',
                          borderColor: task.task_type === 'URGENT' ? 'var(--nqh-chili-600)' : 'var(--nqh-coffee-500)',
                        }}>
                          {task.task_type === 'URGENT' ? 'KHẨN CẤP' : 'PHÁT SINH'}
                        </span>
                        <span className={styles.badge} style={getStatusBadgeStyle(task.status)}>
                          {STATUS_LABEL[task.status] || task.status}
                        </span>
                      </div>

                      {task.description && (
                        <p className={styles.taskDescription}>{task.description}</p>
                      )}

                      <div className={styles.taskMeta}>
                        <span>📅 {new Date(task.shift_date).toLocaleDateString('vi-VN')}</span>
                        <span>👤 {employeeNameById(task.assigned_to)}</span>
                      </div>

                      {/* 2026-08-16: cùng thông tin hoàn thành + ảnh như mục
                          "Hôm nay" — nối vào Lịch sử cho nhất quán. */}
                      {task.status === 'COMPLETED' && (
                        <div className={styles.completionRow}>
                          {task.completed_at && (
                            <span className={styles.completionTime}>
                              ✅ Hoàn thành lúc {formatCompletedAt(task.completed_at)}
                            </span>
                          )}
                          {task.completion_photo_url ? (
                            <button
                              type="button"
                              className={styles.completionThumbBtn}
                              onClick={() => { setZoomPhotoUrl(normalizePhotoUrl(task.completion_photo_url as string)); setZoomedIn(false); }}
                            >
                              <img
                                src={normalizePhotoUrl(task.completion_photo_url)}
                                alt="Ảnh chứng minh hoàn thành"
                                className={styles.completionThumb}
                              />
                              <span className={styles.completionThumbLabel}>🔍 Xem ảnh</span>
                            </button>
                          ) : (
                            <span className={styles.completionNoPhoto}>Không có ảnh</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </div>

      {/* Create Task Modal — SPEC-0418 Section 4.2 */}
      {showCreateModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            style={{
              background: 'white', borderRadius: '16px', padding: '24px',
              maxWidth: '500px', width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal title — SPEC-0418 v1.1.0 §4.2: fixed title, not type-dependent */}
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: 700 }}>
              📋 Giao việc mới
            </h3>

            {/* Task Type Dropdown — SPEC-0418 v1.1.0 §4.2 (RPT-GOV-041 §8) */}
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--nqh-gray-700)', marginBottom: '4px' }}>
              Loại công việc
            </label>
            <select
              value={createTaskType}
              onChange={(e) => setCreateTaskType(e.target.value as 'AD_HOC' | 'URGENT')}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: createTaskType === 'URGENT' ? '2px solid var(--nqh-chili-600)' : '1px solid var(--nqh-gray-300)',
                fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box',
                fontWeight: 600,
                color: createTaskType === 'URGENT' ? 'var(--nqh-chili-600)' : 'var(--nqh-coffee-700)',
                background: createTaskType === 'URGENT' ? 'var(--nqh-chili-50)' : 'white',
              }}
            >
              <option value="AD_HOC">Phát sinh</option>
              <option value="URGENT">Khẩn cấp</option>
            </select>

            {/* URGENT visual indicator — SPEC-0418 v1.1.0 §4.2 */}
            {createTaskType === 'URGENT' && (
              <div style={{
                background: 'var(--nqh-chili-50)', border: '1px solid var(--nqh-chili-200)', borderRadius: '8px',
                padding: '8px 12px', marginBottom: '12px', color: 'var(--nqh-chili-700)', fontSize: '13px',
              }}>
                🚨 Nhiệm vụ Khẩn cấp — sẽ gửi push notification ngay lập tức
              </div>
            )}

            {/* Title */}
            <input
              type="text"
              placeholder="Tiêu đề nhiệm vụ..."
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: '1px solid var(--nqh-gray-300)', fontSize: '14px', marginBottom: '12px',
                boxSizing: 'border-box',
              }}
            />

            {/* Description */}
            <textarea
              placeholder="Mô tả chi tiết (tùy chọn)..."
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              rows={3}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: '1px solid var(--nqh-gray-300)', fontSize: '14px', marginBottom: '12px',
                resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />

            {/* 2026-08-16: chọn NHIỀU nhân viên — panel checkbox thay dropdown đơn cũ */}
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--nqh-gray-700)', marginBottom: '4px' }}>
              Nhân viên được giao
            </label>
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <button
                type="button"
                onClick={() => setAssigneeMenuOpen((v) => !v)}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--nqh-gray-300)', fontSize: '14px', boxSizing: 'border-box',
                  background: 'white', textAlign: 'left', cursor: 'pointer',
                  color: formAssigneeIds.length > 0 ? 'var(--nqh-gray-800)' : 'var(--nqh-gray-400)',
                }}
              >
                {formAssigneeIds.length === 0
                  ? 'Chọn nhân viên...'
                  : `${formAssigneeIds.length} nhân viên đã chọn`}
              </button>
              {assigneeMenuOpen && (() => {
                const q = noDiacritics(assigneeSearch.trim());
                const filteredStaff = q
                  ? staffList.filter((s) => noDiacritics(s.full_name).includes(q))
                  : staffList;
                const allFilteredSelected =
                  filteredStaff.length > 0 && filteredStaff.every((s) => formAssigneeIds.includes(s.id));
                return (
                <div
                  style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                    maxHeight: '280px', display: 'flex', flexDirection: 'column',
                    background: 'white',
                    border: '1px solid var(--nqh-gray-300)', borderRadius: '8px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 10,
                  }}
                >
                  {/* 2026-08-17 (theo yêu cầu người dùng): gõ tên để lọc gợi ý,
                      bỏ dấu vẫn khớp — cùng cách RangeAssignPopover đã làm. */}
                  <input
                    type="text"
                    autoFocus
                    placeholder="Gõ tên nhân viên để tìm…"
                    value={assigneeSearch}
                    onChange={(e) => setAssigneeSearch(e.target.value)}
                    style={{
                      padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--nqh-gray-100)',
                      fontSize: '14px', outline: 'none', boxSizing: 'border-box',
                    }}
                  />
                  <div style={{ overflowY: 'auto' }}>
                    <label
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 12px', fontSize: '13px', fontWeight: 600,
                        borderBottom: '1px solid var(--nqh-gray-100)', cursor: 'pointer', color: 'var(--nqh-coffee-700)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={allFilteredSelected}
                        onChange={(e) => {
                          const filteredIds = filteredStaff.map((s) => s.id);
                          setFormAssigneeIds((prev) =>
                            e.target.checked
                              ? Array.from(new Set([...prev, ...filteredIds]))
                              : prev.filter((id) => !filteredIds.includes(id)),
                          );
                        }}
                      />
                      Chọn tất cả ({filteredStaff.length})
                    </label>
                    {filteredStaff.length === 0 ? (
                      <div style={{ padding: '10px 12px', fontSize: '13px', color: 'var(--nqh-gray-400)' }}>
                        Không tìm thấy nhân viên nào.
                      </div>
                    ) : (
                      filteredStaff.map((s) => (
                        <label
                          key={s.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '8px 12px', fontSize: '14px', cursor: 'pointer',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={formAssigneeIds.includes(s.id)}
                            onChange={(e) =>
                              setFormAssigneeIds((prev) =>
                                e.target.checked ? [...prev, s.id] : prev.filter((id) => id !== s.id),
                              )
                            }
                          />
                          {s.full_name} ({s.email})
                        </label>
                      ))
                    )}
                  </div>
                </div>
                );
              })()}
            </div>

            {/* 2026-08-16: khoảng ngày — chọn cả tuần giao 1 lần thay vì
                từng ngày (mặc định hôm nay→hôm nay, giữ hành vi cũ nếu không đổi) */}
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--nqh-gray-700)', marginBottom: '4px' }}>
              Từ ngày – đến ngày
            </label>
            <div style={{ marginBottom: '12px' }}>
              <DateRangePicker
                value={{ start: formDateFrom, end: formDateTo }}
                onChange={(range) => {
                  setFormDateFrom(range.start ?? formDateFrom);
                  setFormDateTo(range.end ?? range.start ?? formDateTo);
                }}
                maxSpanDays={62}
                hidePresets
              />
            </div>

            {/* 2026-08-16: hạn hoàn thành — theo phút (như cũ, chỉ 1 ngày)
                HOẶC theo ca làm việc THẬT của từng NV/ngày */}
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--nqh-gray-700)', marginBottom: '4px' }}>
              Hạn hoàn thành
            </label>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
              {([
                { mode: 'MINUTES' as const, label: 'Theo phút' },
                { mode: 'START_OF_SHIFT' as const, label: 'Đầu ca' },
                { mode: 'MID_SHIFT' as const, label: 'Giữa ca' },
                { mode: 'END_OF_SHIFT' as const, label: 'Kết ca' },
              ]).map((opt) => (
                <button
                  key={opt.mode}
                  type="button"
                  onClick={() => setFormDeadlineMode(opt.mode)}
                  style={{
                    padding: '6px 12px', borderRadius: '999px', fontSize: '13px', fontWeight: 600,
                    cursor: 'pointer',
                    border: formDeadlineMode === opt.mode ? '1px solid var(--nqh-coffee-500)' : '1px solid var(--nqh-gray-300)',
                    background: formDeadlineMode === opt.mode ? 'var(--nqh-coffee-100)' : 'white',
                    color: formDeadlineMode === opt.mode ? 'var(--nqh-coffee-700)' : 'var(--nqh-gray-500)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {formDeadlineMode === 'MINUTES' ? (
              <select
                value={formDeadlineMinutes}
                onChange={(e) => setFormDeadlineMinutes(Number(e.target.value))}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--nqh-gray-300)', fontSize: '14px', marginBottom: '20px',
                  boxSizing: 'border-box',
                }}
              >
                {DEADLINE_OPTIONS.map((opt) => (
                  <option key={opt.minutes} value={opt.minutes}>{opt.label}</option>
                ))}
              </select>
            ) : (
              <p style={{ fontSize: '12px', color: 'var(--nqh-gray-500)', marginTop: 0, marginBottom: '20px' }}>
                Hạn sẽ tính theo ca làm việc THẬT của từng nhân viên trong khoảng ngày đã chọn ở trên
                ({formDeadlineMode === 'START_OF_SHIFT' ? 'giờ bắt đầu ca' : formDeadlineMode === 'MID_SHIFT' ? 'giữa ca' : 'giờ kết thúc ca'}).
                Nhân viên không có ca ngày nào sẽ bị bỏ qua ngày đó.
              </p>
            )}

            {/* Footer buttons — 2026-09-04: `minHeight:48`+`flex:1` (equal
                width, no longer content-sized+right-aligned) added to match
                mobile AppButton.tsx's non-small default (minHeight:48,
                flex:1) — same pattern as AttendanceModern.module.css
                `.actionsRow > .primaryButton/.secondaryButton{flex:1}`. */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  flex: 1, minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '10px 20px', borderRadius: '8px', cursor: 'pointer',
                  border: '1px solid var(--nqh-gray-300)', background: 'white', color: 'var(--nqh-gray-500)',
                  fontWeight: 600, fontSize: '14px',
                }}
              >
                Hủy
              </button>
              <button
                onClick={handleCreateTask}
                disabled={actionLoading === 'create'}
                style={{
                  flex: 1, minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  background: createTaskType === 'URGENT' ? 'var(--nqh-chili-600)' : 'var(--nqh-coffee-500)',
                  color: 'white', fontWeight: 600, fontSize: '14px',
                  opacity: actionLoading === 'create' ? 0.7 : 1,
                }}
              >
                {actionLoading === 'create' ? 'Đang tạo...' : 'Giao việc'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Task Modal — SPEC-0418 Section 4.4 */}
      {showCompleteModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 10000,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setShowCompleteModal(null)}
        >
          <div
            style={{
              background: 'white', borderRadius: '16px', padding: '24px',
              maxWidth: '450px', width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: '18px', fontWeight: 700 }}>
              Hoàn thành nhiệm vụ
            </h3>
            <p style={{ margin: '0 0 16px', color: 'var(--nqh-gray-500)', fontSize: '14px' }}>
              {tasks.find(t => t.id === showCompleteModal)?.title}
            </p>

            {/* URGENT warning — SPEC-0418 Section 4.4 */}
            {tasks.find(t => t.id === showCompleteModal)?.task_type === 'URGENT' && (
              <div style={{
                background: 'var(--nqh-chili-50)', border: '1px solid var(--nqh-chili-200)', borderRadius: '8px',
                padding: '10px 12px', marginBottom: '12px', color: 'var(--nqh-chili-700)', fontSize: '13px',
              }}>
                ⚠️ Nhiệm vụ KHẨN CẤP — Bắt buộc có ảnh chứng minh hoàn thành
              </div>
            )}

            <textarea
              placeholder="Ghi chú hoàn thành..."
              value={completeNote}
              onChange={(e) => setCompleteNote(e.target.value)}
              rows={3}
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: '1px solid var(--nqh-gray-300)', fontSize: '14px', marginBottom: '12px',
                resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />

            {/* Photo capture — camera/gallery (ISS-016-B fix) */}
            <div style={{ marginBottom: '20px' }}>
              {!completePhotoUrl ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <label style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    padding: '12px', borderRadius: '8px', cursor: 'pointer',
                    border: '2px dashed var(--nqh-gray-300)', background: 'var(--nqh-gray-50)', fontSize: '14px', color: 'var(--nqh-gray-700)',
                    fontWeight: 600, minHeight: '48px',
                  }}>
                    📷 Chụp ảnh
                    <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const reader = new FileReader();
                        reader.onload = () => setCompletePhotoUrl(String(reader.result));
                        reader.readAsDataURL(f);
                      }}
                    />
                  </label>
                  <label style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    padding: '12px', borderRadius: '8px', cursor: 'pointer',
                    border: '2px dashed var(--nqh-gray-300)', background: 'var(--nqh-gray-50)', fontSize: '14px', color: 'var(--nqh-gray-700)',
                    fontWeight: 600, minHeight: '48px',
                  }}>
                    🖼️ Chọn ảnh
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const reader = new FileReader();
                        reader.onload = () => setCompletePhotoUrl(String(reader.result));
                        reader.readAsDataURL(f);
                      }}
                    />
                  </label>
                </div>
              ) : (
                <div style={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--nqh-gray-300)' }}>
                  <img src={completePhotoUrl} alt="Ảnh minh chứng" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', display: 'block' }} />
                  <button
                    type="button"
                    onClick={() => setCompletePhotoUrl('')}
                    style={{
                      position: 'absolute', top: '6px', right: '6px',
                      background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none',
                      borderRadius: '50%', width: '28px', height: '28px', cursor: 'pointer',
                      fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >✕</button>
                </div>
              )}
              {!completePhotoUrl && tasks.find(t => t.id === showCompleteModal)?.task_type === 'URGENT' && (
                <div style={{ fontSize: '12px', color: 'var(--nqh-chili-600)', marginTop: '6px' }}>
                  ⚠️ Bắt buộc chụp ảnh chứng minh cho nhiệm vụ khẩn cấp
                </div>
              )}
            </div>

            {/* 2026-09-04: `minHeight:48`+`flex:1` — see Create Task Modal
                footer note above. */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowCompleteModal(null)}
                style={{
                  flex: 1, minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '10px 20px', borderRadius: '8px', cursor: 'pointer',
                  border: '1px solid var(--nqh-gray-300)', background: 'white', color: 'var(--nqh-gray-500)',
                  fontWeight: 600, fontSize: '14px',
                }}
              >
                Hủy
              </button>
              <button
                onClick={() => handleComplete(showCompleteModal)}
                disabled={actionLoading === showCompleteModal}
                style={{
                  flex: 1, minHeight: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  background: 'var(--nqh-coffee-500)', color: 'white', fontWeight: 600, fontSize: '14px',
                  opacity: actionLoading === showCompleteModal ? 0.7 : 1,
                }}
              >
                {actionLoading === showCompleteModal ? 'Đang xử lý...' : 'Xác nhận hoàn thành'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2026-08-16: Lightbox xem/zoom ảnh chứng minh hoàn thành — bấm ảnh
          để phóng to/thu nhỏ, bấm ✕ hoặc nền tối để đóng. */}
      {zoomPhotoUrl && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', zIndex: 10002,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: zoomedIn ? 'auto' : 'hidden',
          }}
          onClick={() => setZoomPhotoUrl(null)}
        >
          <button
            type="button"
            onClick={() => setZoomPhotoUrl(null)}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none',
              borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer',
              fontSize: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            aria-label="Đóng"
          >✕</button>
          <img
            src={zoomPhotoUrl}
            alt="Ảnh chứng minh hoàn thành — phóng to"
            onClick={(e) => { e.stopPropagation(); setZoomedIn((z) => !z); }}
            style={{
              maxWidth: zoomedIn ? 'none' : '92vw',
              maxHeight: zoomedIn ? 'none' : '88vh',
              width: zoomedIn ? '180%' : 'auto',
              cursor: zoomedIn ? 'zoom-out' : 'zoom-in',
              borderRadius: '8px',
              transition: 'width 0.15s ease',
            }}
          />
          <div style={{
            position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
            color: 'rgba(255,255,255,0.7)', fontSize: '13px',
          }}>
            Bấm vào ảnh để {zoomedIn ? 'thu nhỏ' : 'phóng to'}
          </div>
        </div>
      )}

      {/* Footer — chỉ hiện ở trang độc lập (/tasks, EMPLOYEE); ẩn khi lồng
          trong ManagerLayout vì các trang Manager khác không có footer riêng. */}
      {!embedded && (
        <div className={styles.footer}>
          <p className={styles.footerText}>
            🍽️ Hệ thống quản lý F&B thông minh cho doanh nghiệp Việt Nam
          </p>
          <p className={styles.footerCopyright}>
            © 2026 NQH-Bot Platform — Powered by Nhat Quang Holding
          </p>
        </div>
      )}
    </div>
  );
};
